/**
 * hub-message-listener.ts
 *
 * Injected into each sub-project iframe by the Hub.
 * Listens for "agent_command" messages from the parent Hub window,
 * maps them to existing tool handlers, executes, and posts the result back.
 *
 * This file is loaded automatically when the project-page detects it is
 * running inside an iframe with a Hub parent.
 */

/* ─── Types ─── */
export interface AgentCommand {
    type: 'agent_command';
    requestId: string;
    action: string;
    params: Record<string, any>;
    timestamp: number;
}

export interface CommandResult {
    type: 'command_result';
    requestId: string;
    success: boolean;
    data?: any;
    error?: string;
    timestamp: number;
}

export interface ProjectStateUpdate {
    type: 'project_state';
    projectId: string;
    currentScene?: string;
    currentModel?: string;
    timestamp: number;
}

/* ─── Detect if we are inside a Hub iframe ─── */
export function isInsideHubIframe(): boolean {
    try {
        return window.self !== window.top;
    } catch {
        return true; // cross-origin = we are in an iframe
    }
}

/* ─── Initialise the listener (called once on project-page boot) ─── */
export function initHubMessageListener(): void {
    if (!isInsideHubIframe()) return;

    console.log('[HubMessageListener] Project is inside Hub iframe — attaching listener');

    window.addEventListener('message', async (event: MessageEvent) => {
        const data = event.data;
        if (!data || data.type !== 'agent_command') return;

        const { requestId, action, params } = data as AgentCommand;
        console.log(`[HubMessageListener] Received command: ${action}`, params);

        try {
            const result = await executeToolAction(action, params);
            const response: CommandResult = {
                type: 'command_result',
                requestId,
                success: true,
                data: result,
                timestamp: Date.now(),
            };
            window.parent.postMessage(response, '*');
        } catch (err: any) {
            const response: CommandResult = {
                type: 'command_result',
                requestId,
                success: false,
                error: err?.message || String(err),
                timestamp: Date.now(),
            };
            window.parent.postMessage(response, '*');
        }
    });

    // Notify Hub that the iframe is ready to receive commands
    window.parent.postMessage({
        type: 'project_state',
        projectId: window.id_path || '',
        currentScene: null,
        timestamp: Date.now(),
    } satisfies ProjectStateUpdate, '*');
}

/* ─── Map action name → existing tool execution ─── */
async function executeToolAction(action: string, params: Record<string, any>): Promise<any> {
    // Special meta-action: return list of available tool names so the Hub
    // can dynamically inform Gemini which features the sub-project supports.
    if (action === '__get_available_tools__') {
        try {
            const { tools } = await import('@/agents/gemini-live/lib/tools');
            return {
                success: true,
                tools: Object.keys(tools),
                projectId: window.id_path || '',
            };
        } catch {
            return { success: false, tools: [] };
        }
    }

    // ── Priority 1: try the full Gemini tools registry ──
    // This automatically supports ALL project-level tools (navigate_to_scene,
    // list_scene_names, open_model, show_iot_monitoring_dashboard, etc.)
    // without needing a manual mapping for each one.
    try {
        const { tools } = await import('@/agents/gemini-live/lib/tools');
        const tool = tools[action];
        if (tool) {
            console.log(`[HubMessageListener] Executing tool "${action}" via tools registry`);
            return await tool.handler(params);
        }
    } catch (err) {
        console.warn(`[HubMessageListener] Failed to load tools registry:`, err);
    }

    // ── Priority 2: window-level global functions (legacy fallback) ──
    switch (action) {
        case 'navigate_to_scene': {
            const sceneName = params.scene_name || params.scene_query || params.name || '';
            if (typeof window.navigateToScene === 'function') {
                return await window.navigateToScene(sceneName);
            }
            window.dispatchEvent(new CustomEvent('agent_navigate', { detail: { sceneName } }));
            return { success: true, message: `Navigate requested: ${sceneName}` };
        }

        case 'open_3d_model': {
            const modelName = params.model_name || params.name || '';
            if (typeof window.handleOpenMaterialDetail === 'function') {
                window.handleOpenMaterialDetail(modelName, '3d_model');
                return { success: true };
            }
            window.dispatchEvent(new CustomEvent('agent_open_model', { detail: params }));
            return { success: true, message: `Model open requested: ${modelName}` };
        }

        case 'get_current_scene_info': {
            if (typeof window.getCurrentSceneInfo === 'function') {
                return await window.getCurrentSceneInfo();
            }
            return { scene: null, message: 'getCurrentSceneInfo not available' };
        }

        case 'list_scenes': {
            if (typeof window.listScenes === 'function') {
                return await window.listScenes();
            }
            return { scenes: [], message: 'listScenes not available' };
        }

        default:
            // Generic dispatch — let the existing tool infrastructure pick it up
            window.dispatchEvent(new CustomEvent('agent_tool_call', {
                detail: { action, params },
            }));
            return { success: true, message: `Action dispatched: ${action}` };
    }
}

/* ─── Window type extensions ─── */
declare global {
    interface Window {
        id_path?: string;
        navigateToScene?: (name: string) => Promise<any>;
        handleOpenMaterialDetail?: (...args: any[]) => void;
        getCurrentSceneInfo?: () => Promise<any>;
        listScenes?: () => Promise<any>;
    }
}

